import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { leadpagesAPI } from '../services/api';
import LeadpagePreviewModal, { formatLabel } from '../components/LeadpagePreviewModal';
import './Leadpages.css';

const DOMAIN_STATUS_ICONS = { wanted: '🟡', purchased: '🟢', attached: '🔵' };

const downloadBlob = (res, fallbackName) => {
    const dispo = res.headers['content-disposition'] || '';
    const match = dispo.match(/filename="?([^";]+)"?/);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = match ? match[1] : fallbackName;
    a.click();
    URL.revokeObjectURL(url);
};

function DomainEditor({ page, onChanged, onError }) {
    const domain = page.domain;
    const [name, setName] = useState(domain?.name || '');
    const [busy, setBusy] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed || busy) return;
        setBusy(true);
        try {
            if (domain) {
                await leadpagesAPI.updateDomain(domain.id, { name: trimmed });
            } else {
                await leadpagesAPI.createDomain({ name: trimmed, leadpage_id: page.id });
            }
            await onChanged();
        } catch (err) {
            onError(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Failed to save the domain');
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (status) => {
        if (!domain || busy) return;
        setBusy(true);
        try {
            await leadpagesAPI.updateDomain(domain.id, { status });
            await onChanged();
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to update the domain status');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!domain || busy) return;
        setBusy(true);
        try {
            await leadpagesAPI.deleteDomain(domain.id);
            setName('');
            await onChanged();
        } catch (err) {
            onError(err.response?.data?.detail || 'Failed to remove the domain');
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="lpm-domain" onSubmit={save}>
            <span className="lp-item-label">Domain</span>
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="my-newsletter.com"
                disabled={busy}
            />
            {(!domain || name.trim() !== domain.name) && (
                <button type="submit" className="btn-secondary" disabled={busy || !name.trim()}>
                    {domain ? 'Rename' : 'Save'}
                </button>
            )}
            {domain && (
                <>
                    <select
                        value={domain.status}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled={busy}
                        title="Domain status"
                    >
                        <option value="wanted">{DOMAIN_STATUS_ICONS.wanted} wanted</option>
                        <option value="purchased">{DOMAIN_STATUS_ICONS.purchased} purchased</option>
                        <option value="attached">{DOMAIN_STATUS_ICONS.attached} attached</option>
                    </select>
                    <button type="button" className="lp-delete-btn" title="Remove domain" onClick={remove} disabled={busy}>
                        ✕
                    </button>
                </>
            )}
        </form>
    );
}

function ManagedPageRow({ page, adapters, onPreview, onChanged, onError }) {
    const [busy, setBusy] = useState(null); // 'export' | 'duplicate' | 'delete'
    const cloudAdapters = adapters.filter((a) => a.key !== 'download');
    const actionable = page.status === 'draft' || page.status === 'selected' || page.status === 'exported';

    const run = async (kind, fn) => {
        if (busy) return;
        setBusy(kind);
        try {
            await fn();
            await onChanged();
        } catch (err) {
            onError(err.response?.data?.detail || `Failed to ${kind} the leadpage`);
        } finally {
            setBusy(null);
        }
    };

    const exportPackage = () => run('export', async () => {
        const res = await leadpagesAPI.exportLeadpage(page.id);
        downloadBlob(res, `${formatLabel(page.format_key).replaceAll(' ', '-')}.zip`);
    });

    const duplicate = () => run('duplicate', () => leadpagesAPI.duplicateLeadpage(page.id));

    const remove = () => {
        if (!window.confirm('Delete this leadpage and its whole version history?')) return;
        run('delete', () => leadpagesAPI.deleteLeadpage(page.id));
    };

    return (
        <div className={`lpm-row glass ${page.status === 'exported' ? 'exported' : ''}`}>
            <div className="lpm-row-main">
                <div className="lpm-row-title">
                    <strong>{formatLabel(page.format_key)}</strong>
                    <span className={`lp-group-badge lpm-status-${page.status}`}>{page.status}</span>
                    {page.edit_in_progress && <span className="lp-spinner lp-spinner-sm" title="AI edit in progress" />}
                </div>
                {page.headline && <p className="lpm-headline">«{page.headline}»</p>}
                {page.status === 'failed' && page.status_message && (
                    <p className="lp-error">{page.status_message}</p>
                )}
                <DomainEditor page={page} onChanged={onChanged} onError={onError} />
            </div>

            {actionable && (
                <div className="lpm-row-actions">
                    <div className="lpm-actions-line">
                        <button className="btn-secondary" onClick={() => onPreview(page)}>Preview & edit</button>
                        <button className="btn-secondary" onClick={duplicate} disabled={!!busy}>
                            {busy === 'duplicate' ? 'Duplicating…' : 'Duplicate'}
                        </button>
                        <button className="btn-primary" onClick={exportPackage} disabled={!!busy}>
                            {busy === 'export' ? 'Packing…' : '⬇ Export package'}
                        </button>
                        <button className="lp-delete-btn lpm-delete" title="Delete" onClick={remove} disabled={!!busy}>
                            ✕
                        </button>
                    </div>
                    <div className="lpm-deploy-line">
                        <span className="lp-item-label">Deploy</span>
                        {cloudAdapters.map((adapter) => (
                            <button
                                key={adapter.key}
                                className="btn-secondary lpm-deploy-btn"
                                disabled={!adapter.enabled}
                                title={adapter.description}
                            >
                                {adapter.name}
                            </button>
                        ))}
                        <button className="btn-secondary lpm-deploy-btn" disabled title="Available once deploys are live (deploy milestone)">
                            Unpublish
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function LeadpageManager() {
    const [groups, setGroups] = useState([]);
    const [unassignedDomains, setUnassignedDomains] = useState([]);
    const [adapters, setAdapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);

    const reload = useCallback(async () => {
        try {
            const res = await leadpagesAPI.getManage();
            setGroups(res.data.groups || []);
            setUnassignedDomains(res.data.unassigned_domains || []);
        } catch {
            setError('Failed to load the leadpage registry');
        }
    }, []);

    useEffect(() => {
        Promise.all([
            reload(),
            leadpagesAPI.getDeployAdapters()
                .then((res) => setAdapters(res.data))
                .catch(() => setAdapters([])),
        ]).finally(() => setLoading(false));
    }, [reload]);

    const handlePreview = async (page) => {
        try {
            const res = await leadpagesAPI.getLeadpageHtml(page.id);
            setPreview({ page, html: res.data });
        } catch {
            setError('Failed to load the preview');
        }
    };

    const handleToggleTake = async (page) => {
        const next = page.status === 'selected' ? 'draft' : 'selected';
        try {
            const res = await leadpagesAPI.updateLeadpage(page.id, { status: next });
            setPreview((prev) => (prev && prev.page.id === page.id ? { ...prev, page: res.data } : prev));
            await reload();
        } catch {
            setError('Failed to update the selection');
        }
    };

    const handlePageUpdate = useCallback((updated) => {
        setPreview((prev) => (prev && prev.page.id === updated.id ? { ...prev, page: updated } : prev));
        reload();
    }, [reload]);

    const total = groups.reduce((acc, g) => acc + g.leadpages.length, 0);

    return (
        <div className="leadpages-page">
            <div className="lp-header">
                <h1><span className="gradient-text">Leadpage control room</span></h1>
                <p>Everything produced, by blog — preview, edit, duplicate, export, delete</p>
                <Link to="/leadpages" className="btn-secondary lp-manage-link">← Back to generation</Link>
            </div>

            {error && (
                <div className="lp-failed glass">
                    <p>⚠️ {String(error)}</p>
                    <button className="btn-secondary" onClick={() => { setError(null); reload(); }}>Dismiss</button>
                </div>
            )}

            {loading && (
                <div className="lp-analyzing glass">
                    <div className="lp-spinner" />
                    <div><strong>Loading the registry…</strong></div>
                </div>
            )}

            {!loading && total === 0 && (
                <div className="lp-analyzing glass">
                    <div>
                        <strong>Nothing produced yet</strong>
                        <p>Generate your first pack on the <Link to="/leadpages">Leadpages</Link> screen.</p>
                    </div>
                </div>
            )}

            {groups.map((group) => (
                group.leadpages.length > 0 && (
                    <div key={group.source.id} className="lpm-group">
                        <div className="lpm-group-header">
                            <h3>{group.source.title || group.source.url}</h3>
                            <span className="lp-source-vertical">{group.source.vertical || ''}</span>
                            <span className="lp-plan-meta">
                                {group.leadpages.length} landing{group.leadpages.length === 1 ? '' : 's'}
                            </span>
                        </div>
                        {group.leadpages.map((page) => (
                            <ManagedPageRow
                                key={page.id}
                                page={page}
                                adapters={adapters}
                                onPreview={handlePreview}
                                onChanged={reload}
                                onError={setError}
                            />
                        ))}
                    </div>
                )
            ))}

            {unassignedDomains.length > 0 && (
                <div className="lpm-group">
                    <div className="lpm-group-header">
                        <h3>Domains without a landing</h3>
                        <span className="lp-plan-meta">kept after their landings were deleted</span>
                    </div>
                    {unassignedDomains.map((domain) => (
                        <div key={domain.id} className="lpm-row glass">
                            <div className="lpm-row-main">
                                <div className="lpm-row-title">
                                    <strong>{domain.name}</strong>
                                    <span className="lp-group-badge">{DOMAIN_STATUS_ICONS[domain.status]} {domain.status}</span>
                                </div>
                            </div>
                            <div className="lpm-row-actions">
                                <button
                                    className="lp-delete-btn lpm-delete"
                                    title="Remove domain"
                                    onClick={() => leadpagesAPI.deleteDomain(domain.id).then(reload)}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {preview && (
                <LeadpagePreviewModal
                    key={preview.page.id}
                    page={preview.page}
                    html={preview.html}
                    onClose={() => { setPreview(null); reload(); }}
                    onToggleTake={handleToggleTake}
                    onPageUpdate={handlePageUpdate}
                />
            )}
        </div>
    );
}

export default LeadpageManager;
